/** A row that can be attributed to one of a PDF's contract documents. */
export interface ContractDocumentAttributed {
  contractDocumentUuid?: string | null;
  contractDocumentName?: string | null;
}

/** One contract document's rows, in the order they arrived. */
export interface ContractDocumentGroup<T> {
  /** The contract document uuid, or `''` for rows with no owning document. */
  key: string;
  /** Display name for the group heading; absent when the document can't be named. */
  label?: string | null;
  rows: T[];
}

/**
 * Groups rows by their contract document, preserving both the incoming order of
 * the groups and of the rows within each. The shared validation sort already
 * emits items document by document in PDF order (see
 * `sortValidationItemsByPage`), so first-appearance order IS PDF order.
 *
 * Grouping is load-bearing rather than decorative: each row's page label is
 * relative to its own contract document (BLO-13411), so page numbers restart at
 * 1 per document and an unlabelled list shows several indistinguishable
 * "Page 1" rows.
 *
 * Rows with no contract document collect into a single group keyed `''`, which
 * callers render without a heading.
 */
export function groupByContractDocument<T extends ContractDocumentAttributed>(
  rows: T[]
): ContractDocumentGroup<T>[] {
  const groups: ContractDocumentGroup<T>[] = [];
  const indexByKey = new Map<string, number>();

  rows.forEach((row) => {
    const key = row.contractDocumentUuid ?? '';
    const existing = indexByKey.get(key);

    if (existing != null) {
      groups[existing].rows.push(row);

      return;
    }

    indexByKey.set(key, groups.length);
    groups.push({ key, label: row.contractDocumentName, rows: [row] });
  });

  return groups;
}

/**
 * Whether a grouped list should render per-document headings.
 *
 * Decided by how many contract documents the PDF *holds*
 * (`contractDocumentCount`), not how many produced rows: a four-document PDF
 * where only the addendum has findings still needs its heading, because that
 * row's "Page 1" means page 1 of the addendum — which may be page 14 of the file
 * the reader is looking at. Inferring from `groups.length` suppressed exactly
 * that label.
 *
 * Falls back to the group count when the caller can't supply the real count, so
 * a heading is still shown once two documents contribute rows.
 */
export function shouldShowContractDocumentHeadings<T>(
  groups: ContractDocumentGroup<T>[],
  contractDocumentCount?: number
): boolean {
  const documentCount = contractDocumentCount ?? groups.length;

  return documentCount > 1 && groups.some((group) => group.label);
}
